/* ATTENTION: THIS is is only for use in root legacy app,
 * not in separated modules, if you want to provide auth in modules,
 * create auth container in module app
 *
 * ALSO: only context providers are good to be imported across the app, not otherwise
 */

import {
  AuthContextDataStore,
  AuthContextStorage,
  AuthContextStore,
  AuthTokenStore,
  decodeJwtToken,
  getAccessTokenFromStorage,
  getClinicIdFromStorage,
  getRefreshTokenFromStorage,
  getSessionIdFromStorage,
  isExternalUserToken,
  setAccessTokenToStorage,
  setClinicIdToStorage,
  setRefreshTokenToStorage,
} from '@surglogs-app/auth'
import {
  createAuthHeaders,
  convertEndpointProviderToFetchArgs,
  EnvApiResolver,
  ENDPOINT_CLINICS_MANAGER_CLINICS,
  ClinicAPIEntity,
  DEFAULT_CLINICS_MANAGER_API_VERSION,
  getAccessManagementClinicUser,
  UserAPIEntity,
  AccessManagementClinicUserGetResponse,
} from '@surglogs-app/network'
import {
  getDashboardRoute,
  getUserDeactivatedRoute,
} from '@surglogs-app/routing'
import type {
  AuthContextData,
  AuthDataFetcherParams,
} from '@surglogs-app/types'

import { config } from '../app/config'
import { deriveClinicListSorted } from '../derivations/clinicList'

// This is a workaround for legacy reasons, we need to clear access token if we are logged in with new auth but still have old access token in storage.
// This is edge case that happened to users during dual mode support for JWT and legacy auth. Can be removed once we migrate from dual mode to JWT only.
const previouslyStoredAccessToken = getAccessTokenFromStorage()

if (
  previouslyStoredAccessToken &&
  window.location.pathname.includes('login/exchange')
) {
  setAccessTokenToStorage(null)
  setRefreshTokenToStorage(null)
}

class AuthContextDataStoreWithoutLogger<T> extends AuthContextDataStore<T> {
  logger = () => {}
}

/**
 * NOTE: dont use as import across the app, this is only for legacy reasons
 */
const AuthContextStoreInstance = new AuthContextStore()

/**
 * @deprecated - dont use as import across the app, this is only for legacy reasons
 */
export const AuthContextStorageInstance = new AuthContextStorage(
  window.sessionStorage,
  AuthContextStoreInstance,
  window.localStorage,
)
/**
 * @deprecated - dont use as import across the app, this is only for legacy reasons
 */
export const AuthTokenStoreInstance = new AuthTokenStore()

const createJwtAuthDataFetcher = async ({ token }: AuthDataFetcherParams) => {
  const decodedToken = decodeJwtToken(token)
  const userId = Number(decodedToken?.surglogs_user_id)
  const clinicId = Number(getClinicIdFromStorage(userId))

  if (!userId || !clinicId) {
    return { user: {} as UserAPIEntity }
  }

  const fetcher = convertEndpointProviderToFetchArgs({
    baseUrl: EnvApiResolver.getUrl(),
    version: config.appVersion,
    headers: createAuthHeaders(token),
  })

  const userDetailResponse = await getAccessManagementClinicUser({
    clinicId,
    userId,
  })(async (request) => {
    const response = await fetch(
      ...fetcher({ method: request.method, url: request.url }),
    )

    if (response.ok) {
      const data = JSON.parse(await response.text())

      return data
    } else {
      const getClinicsResponse = await fetch(
        ...fetcher({
          method: request.method,
          url: {
            endpoint: ENDPOINT_CLINICS_MANAGER_CLINICS,
            params: [DEFAULT_CLINICS_MANAGER_API_VERSION],
          },
        }),
      )

      const data = JSON.parse(await getClinicsResponse.text())
      const clinicToSwitchTo = deriveClinicListSorted(
        data?.clinics ?? [],
      ).filter((clinic: ClinicAPIEntity) => clinic.id !== clinicId)

      if (clinicToSwitchTo.length > 0) {
        setClinicIdToStorage(clinicToSwitchTo[0]?.id, userId)

        // we don't want to redirect EXTERNAL users to dashboard
        if (!isExternalUserToken(token)) {
          window.location.pathname = getDashboardRoute()
        }
      } else {
        setClinicIdToStorage(null, userId)

        if (window.location.pathname !== getUserDeactivatedRoute()) {
          window.location.pathname = getUserDeactivatedRoute()
        }
      }

      return {} as UserAPIEntity
    }
  })

  const tokenInfo: { user: AccessManagementClinicUserGetResponse } = {
    user: userDetailResponse,
  }

  return tokenInfo
}

export const createAuthProvider = (): Auth => {
  const AuthContextDataStoreInstance =
    new AuthContextDataStoreWithoutLogger<AuthContextData>(
      createJwtAuthDataFetcher,
      AuthTokenStoreInstance,
      AuthContextStoreInstance,
    )

  const storageToken = getAccessTokenFromStorage()
  const storageRefreshToken = getRefreshTokenFromStorage()
  const sessionId = getSessionIdFromStorage()

  AuthContextDataStoreInstance.onValidChange(({ user }) => {
    // eslint-disable-next-line no-console
    console.log('AuthContextDataStoreInstance onValidChange', {
      // cannot user typeof, because typeof null is also object
      user: !!user,
      authData: !!AuthContextStoreInstance.getData(),
    })

    // NOTE: just fallback if local storage was cleared
    if (!AuthContextStoreInstance.getUserId() && user.id && !!storageToken) {
      AuthContextStoreInstance.setUserId(user.id)
    }
  })

  AuthTokenStoreInstance.onChange(({ token, refreshToken }) => {
    if (token) {
      setAccessTokenToStorage(token)
      setRefreshTokenToStorage(refreshToken)
    } else {
      setAccessTokenToStorage(null)
      setRefreshTokenToStorage(null)
      AuthContextStoreInstance.clearUserId()
      AuthTokenStoreInstance.clear()
    }
  })

  if (storageToken) {
    AuthTokenStoreInstance.setToken(storageToken, storageRefreshToken)
    AuthContextStorageInstance.sync()
  }

  if (sessionId) {
    AuthContextStoreInstance.setSessionId(sessionId)
  }

  return {
    tokenStore: AuthTokenStoreInstance,
    contextStore: AuthContextStoreInstance,
    contextDataStore: AuthContextDataStoreInstance,
  }
}
